import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { styled } from '@mui/material/styles';

import { 
    Button, 
    IconButton, 
    Menu, 
    MenuItem, 
    CircularProgress, 
    Box, 
    Typography, 
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    Divider
} from '@mui/material';

import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";

import PhoneIcon from '@mui/icons-material/Phone';
import PersonIcon from '@mui/icons-material/Person';
import MoreVertIcon from '@mui/icons-material/MoreVert'
import LoginIcon from '@mui/icons-material/Login'
import LogoutIcon from '@mui/icons-material/Logout'
import PauseIcon from '@mui/icons-material/Pause'
import PlayIcon from '@mui/icons-material/PlayArrow'
import CopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';

import Webphone from '../../Webphone';
import { useWebphoneTabLock } from "../../Webphone/WebphoneTabLock";
import LegacyWebphone from '../../LegacyWebphone';
import useNotification from "../../Notification";
import { useTelephony } from '../../../contexts/TelephonyContext';

import { getSession } from "../../../auth";
import {
    checkTelephonyTab,
    isInstantVoiceIntegrated as hasInstantVoiceIntegration,
    shouldUseNewWebphone
} from '../../Telephony/Utils';

import api from "../../../api";

import "./index.css";

import io from 'socket.io-client';
import formatPhone from "../../../utils/format_phone"

// === Call History persistence (centralizado no WebphoneMenu) ===
const HARD_LIMIT = 100;

const broadcastHistoryRefresh = () => {
    try {
        window.dispatchEvent(new CustomEvent('callhistory:refresh'));
    } catch { }
};

const getHistoryStorageKey = () => {
    const u = getSession?.();
    const userId = u?.profile?.id || "anon";
    return `telephony:callHistory:${userId}`;
};

const readHistory = () => {
    try {
        const raw = localStorage.getItem(getHistoryStorageKey());
        if (!raw) return [];
        const arr = JSON.parse(raw);
        if (!Array.isArray(arr)) return [];
        // rehidrata datas
        return arr.map((x) => ({
            ...x,
            data: x.data ? new Date(x.data) : null,
            startedAt: x.startedAt ? new Date(x.startedAt) : null,
            connectedAt: x.connectedAt ? new Date(x.connectedAt) : null,
            endedAt: x.endedAt ? new Date(x.endedAt) : null,
        }));
    } catch {
        return [];
    }
};

const writeHistory = (list) => {
    try {
        localStorage.setItem(
            getHistoryStorageKey(),
            JSON.stringify(list.slice(0, HARD_LIMIT))
        );
        broadcastHistoryRefresh();
    } catch (e) {
        console.warn("persist history failed:", e);
    }
};

const toDateFromTimestamp = (ts) => {
    const n = Number(ts);
    if (!n || Number.isNaN(n)) return new Date();
    return String(ts).length <= 10 ? new Date(n * 1000) : new Date(n);
};

const translateDirection = (d = "") =>
    (d.toUpperCase() === "OUTBOUND" ? "Saínte" : "Entrante");

const pickDirection = (newDir, oldDir) => {
    if (!newDir || !newDir.trim()) return oldDir || "";
    const up = newDir.toUpperCase();
    if (["BYCALLER", "BYAGENT", "BYQUEUE"].includes(up)) return oldDir || "";
    return up;
};

const pickCallerId = (nc, oc) => {
    if (!nc || !String(nc).trim()) return oc || "";
    if (!oc || !String(oc).trim()) return String(nc).trim();
    return oc;
};

// mesma regra que você usa no Sidebar:
const normalizePhone = (number) => {
    if (!number) return "";
    let n = String(number).trim().replace(/\D/g, "");
    if (n.startsWith("55") && n.length > 11) n = n.slice(2);
    n = n.replace(/^0+/, "");
    return n.length >= 10 ? `+55${n}` : n;
};

// mapeia a mensagem crua p/ shape do histórico (igual ao Sidebar)
const mapMessage = (m) => {
    const {
        type, direction, agent, uniqueid, callerid,
        queue, clientcode, timestamp, linenumber
    } = m;

    const id = uniqueid || `call-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    const tsDate = toDateFromTimestamp(timestamp);

    return {
        id,
        rawType: type,
        direction: direction || "",
        translatedDirection: translateDirection(direction || ""),
        agent: agent ? agent.split("_").pop() : "",
        contactName: clientcode && clientcode.trim() ? clientcode.trim() : "",
        phoneNumber: callerid || "",
        data: tsDate,
        startedAt: tsDate,
        connectedAt: null,
        endedAt: null,
        billsec: 0,
        motivo: queue || "",
        queue: queue || "",
        linenumber: linenumber || "",
        ended: type === "HANGUP",
        connected: type === "CONNECT",
        answered: false,
    };
};

// aplica NEWCALL / CONNECT / HANGUP no array e retorna a nova lista
const upsertFromMessage = (list, msg) => {
    const m = mapMessage(msg);
    const idx = list.findIndex((x) => x.id === m.id);

    if (msg.type === "NEWCALL") {
        if (idx === -1) {
            const finalDirection = (m.direction || "").toUpperCase();
            const item = {
                ...m,
                direction: finalDirection,
                translatedDirection: translateDirection(finalDirection),
                answered: false,
                ended: false,
            };
            return [item, ...list].slice(0, HARD_LIMIT);
        } else {
            const clone = list.slice();
            const old = clone[idx];
            const finalDirection = pickDirection(m.direction, old.direction);
            clone[idx] = {
                ...old,
                ...m,
                direction: finalDirection,
                translatedDirection: translateDirection(finalDirection),
                answered: old.answered ?? false,
                ended: false,
                startedAt: old.startedAt || m.startedAt,
                phoneNumber: pickCallerId(m.phoneNumber, old.phoneNumber),
            };
            const [updated] = clone.splice(idx, 1);
            return [updated, ...clone].slice(0, HARD_LIMIT);
        }
    }

    if (msg.type === "CONNECT") {
        if (idx === -1) {
            // defesa (você disse que não ocorre)
            const finalDirection = (m.direction || "").toUpperCase();
            const item = {
                ...m,
                direction: finalDirection,
                translatedDirection: translateDirection(finalDirection),
                answered: true,
                connected: true,
                connectedAt: m.startedAt,
            };
            return [item, ...list].slice(0, HARD_LIMIT);
        } else {
            const clone = list.slice();
            const old = clone[idx];
            const finalDirection = pickDirection(m.direction, old.direction);
            clone[idx] = {
                ...old,
                connected: true,
                answered: true,
                direction: finalDirection,
                translatedDirection: translateDirection(finalDirection),
                phoneNumber: pickCallerId(m.phoneNumber, old.phoneNumber),
                connectedAt: m.startedAt || old.connectedAt || null,
            };
            const [updated] = clone.splice(idx, 1);
            return [updated, ...clone].slice(0, HARD_LIMIT);
        }
    }

    if (msg.type === "HANGUP") {
        const endedAt = m.startedAt || m.data || new Date();
        if (idx === -1) {
            const finalDirection = (m.direction || "").toUpperCase();
            const item = {
                ...m,
                ended: true,
                endedAt,
                direction: finalDirection,
                translatedDirection: translateDirection(finalDirection),
                answered: false,
                billsec: 0,
            };
            return [item, ...list].slice(0, HARD_LIMIT);
        } else {
            const clone = list.slice();
            const old = clone[idx];
            const finalDirection = old.direction ? old.direction : pickDirection(m.direction, old.direction);
            const startedAt = old.startedAt || old.data;
            const connectedAt = old.connectedAt || null;

            let billsec = 0;
            if (connectedAt) {
                billsec = Math.max(0, Math.floor((endedAt.getTime() - connectedAt.getTime()) / 1000));
            }

            clone[idx] = {
                ...old,
                ended: true,
                endedAt,
                direction: finalDirection,
                translatedDirection: translateDirection(finalDirection),
                data: old.data || startedAt || endedAt,
                phoneNumber: pickCallerId(m.phoneNumber, old.phoneNumber),
                answered: old.answered === true ? true : false,
                billsec,
            };
            const [updated] = clone.splice(idx, 1);
            return [updated, ...clone].slice(0, HARD_LIMIT);
        }
    }

    return list.slice(0, HARD_LIMIT);
};

// lookup de contato no NEWCALL e patch no storage (igual sua lógica atual)
const extractIvContactName = (data) => {
    if (!data) return "";
    if (Array.isArray(data.agent_contacts) && data.agent_contacts.length > 0) {
        return data.agent_contacts[0]?.name || "";
    }
    if (Array.isArray(data.extension_contacts) && data.extension_contacts.length > 0) {
        return data.extension_contacts[0]?.description || "";
    }
    if (Array.isArray(data.queue_contacts) && data.queue_contacts.length > 0) {
        return data.queue_contacts[0]?.description || "";
    }
    return "";
};

const patchContactNameAsync = async ({ enterpriseId, agentId, caller, callId, uniqueidKey, direction }) => {
    if (!enterpriseId || !caller) return;
    try {
        const cacheKey = uniqueidKey || callId;
        window.__telephonyCache = window.__telephonyCache || { contacts: new Map() };
        // decide endpoint: curto/com "_" => iv_contacts; senão => contacts/retrieve
        const hasUnderscore = caller.includes("_");
        const digitsOnly = caller.replace(/\D/g, "");

        if (hasUnderscore || digitsOnly.length < 8) {
            let searchValue = caller;
            if (hasUnderscore) {
                const parts = caller.split("_");
                searchValue = parts[1] ? parts[1] : parts[0];
            }
            const res = await api.get(
                `/api/enterprises/${enterpriseId}/agents/${agentId}/iv_contacts/?limit=1&offset=0&contact_type=ALL&search=${encodeURIComponent(
                    searchValue
                )}`
            );
            const name = extractIvContactName(res.data);
            const accountcodeDescription = res.data.accountcode_description || null;
            window.__telephonyCache.contacts.set(cacheKey, {
                contact: (name || accountcodeDescription)
                    ? { name: name || null, contact_enterprise_name: accountcodeDescription || null }
                    : null,
                phone: searchValue
            });
            if (name || accountcodeDescription) {
                const cur = readHistory();
                const i = cur.findIndex((x) => x.id === callId);
                if (i !== -1) {
                    const clone = cur.slice();
                    clone[i] = { 
                        ...clone[i], 
                        contactName: name,
                        contactEnterpriseName: accountcodeDescription
                     };
                    writeHistory(clone); // dispara 'storage' nas outras abas
                }
            }
        } else {
            const normalized = formatPhone(caller);            
            console.log("CRM lookup for:", caller, normalized);
            const res = await api.get(
                `api/enterprises/${enterpriseId}/contacts/?search=${encodeURIComponent(
                    direction === "OUTBOUND" ? caller : normalized
                )}`
            );
            if (res.status >= 200 && res.status < 300) {
                const results = res.data.results || res.data || [];
                const contact = results[0] || null;
                window.__telephonyCache.contacts.set(cacheKey, { contact, phone: normalized });
                
                const cur = readHistory();
                const i = cur.findIndex((x) => x.id === callId);
                if (i !== -1) {
                    const clone = cur.slice();
                    clone[i] = { 
                        ...clone[i], 
                        contactName: contact?.name || null,
                        contactEnterpriseName: contact?.contact_enterprise_name || null
                    };
                    writeHistory(clone);
                }
            }
        }
    } catch (err) {
        console.warn("CRM lookup failed:", err);
        window.__telephonyCache = window.__telephonyCache || { contacts: new Map() };
        const cacheKey = uniqueidKey || callId;        
        window.__telephonyCache.contacts.set(cacheKey, { contact: null, phone: formatPhone(caller) });
    }
};

const makeAttemptId = (base = "in") =>
    `att-${base}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;


const isAutoOpenCtiUrl = (value) => {
    if (!value) return false;
    return /^https?:\/\/\S+/i.test(String(value).trim());
};

const getAutoOpenCtiUrl = (ctiInfo) => {
    let values = [];

    if (Array.isArray(ctiInfo)) {
        values = ctiInfo;
    } else if (ctiInfo && typeof ctiInfo === 'object') {
        if (Array.isArray(ctiInfo.cti)) {
            values = ctiInfo.cti;
        } else if (ctiInfo.cti) {
            values = [ctiInfo.cti];
        }
    } else if (ctiInfo) {
        values = [ctiInfo];
    }

    const url = values
        .map((value) => String(value || '').trim())
        .find(isAutoOpenCtiUrl);

    return url || null;
};

const publishVoice = (type, detail = {}) => {
    try {
        window.dispatchEvent(new CustomEvent('voice:any', { detail: { type, ...detail } }));
        window.dispatchEvent(new CustomEvent(`voice:${type}`, { detail }));
    } catch { }
};

const KwikTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
    [`& .${tooltipClasses.tooltip}`]: {
        fontSize: 15,
        padding: 10
    },
}));

const WebphoneMenu = () => {

    const user = getSession();
    const autoOpenCtiUrlEnabled = user?.profile?.enterprise?.auto_open_cti_url !== false;
    const location = useLocation();

    const iconButton = React.useRef(null);

    const [anchorEl, setAnchorEl] = React.useState(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [alreadyConnected, setAlreadyConnected] = React.useState(false);
    const [lastVoiceSocketResponse, setLastVoiceSocketResponse] = React.useState(null);

    const [anchorElActions, setAnchorElActions] = React.useState(null)
    const [openDialog, setOpenDialog] = React.useState(false)

    const [pauseReasons, setPauseReasons] = React.useState([])
    const [agentStatus, setAgentStatus] = React.useState()
    const [pauseReason, setPauseReason] = React.useState()
    const [showCti, setShowCti] = React.useState(false)
    const [cti, setCti] = React.useState([])
    const [direction, setDirection] = React.useState("inbound")
    const {
        callStatus,
        dialNumber,
        uniqueid,
        callDirection,
        setCallCti,
        setUniqueid,
        setCallDirection,
        widgetSignal,
        sipSignal,
        incomingData,
        ctiData,
    } = useTelephony();
    const { isOwner } = useWebphoneTabLock();

    const shouldAllowPageClickThrough = callStatus?.status === 'ringing' && !checkTelephonyTab();

    const reAnchor = async () => {
        handleClose();
        await new Promise(r => setTimeout(r, 500));
        handleClick();
    }

    const handleClick = () => {
        setAnchorEl(iconButton.current);
        const display = document.getElementById('dialpad-input');
        if (display) {
            window.setTimeout(() => {
                display.focus();
            }, 300);
        }
        window.setLink = false;
    };

    const handleClose = () => {        
        setAnchorEl();
    };

    useEffect(() => {
        if (checkTelephonyTab()) {
            if (anchorEl) {
                setAnchorEl();
            }
            return;
        }

        if (callStatus?.status !== 'ringing') return;
        if (anchorEl || !iconButton.current) return;

        setAnchorEl(iconButton.current);
    }, [location.pathname, location.search, callStatus?.status, anchorEl]);

    const handleLoading = (val) => {
        setIsLoading(val);
    };

    // Auto-open the webphone popup when an outbound call is initiated from a non-telephony tab
    useEffect(() => {
        if (callStatus.status === 'dialing' && !checkTelephonyTab()) {
            reAnchor();
        }
    }, [callStatus.status]);

    const handleActionsClick = (event) => {
        setAnchorElActions(event.currentTarget)
    }

    const handleActionsClose = () => {
        setAnchorElActions()
    }

    const handlePauseReasons = () => {
        setOpenDialog(true)
        handleActionsClose()
    }

    const handleDialogClose = () => {
        setOpenDialog(false)
    }

    const handleReasonSubmit = () => {
        if (! isInstantVoiceIntegrated())
            return

        if (! pauseReason) {
            useNotification(
                'Atenção!',
                'Selecione o motivo da pausa.',
                'warning'
            );
            return
        }

        api.post(`api/IV/agents/${user.profile.id}/instantvoice_agent_pause/`, {
            reason: pauseReason
        }).then((res) => {
            if (res.status === 200) {
                setPauseReason()
                setOpenDialog(false)
            }
        }).catch((err) => {})
    }

    const registerCall = (message, direction) => {
        const agentId = getSession().profile.agent_id
        api.post(`api/IV/agents/${agentId}/register_call`, {
            unique_id: message["uniqueid"],
            caller_id: message["callerid"],
            direction: direction
        })
    }

    let connectVoiceSocket = () => {
        let socket = null; 
        let keepAliveVoice = null;
        
        const options = { upgrade: false, transports: [ 'websocket' ] };        
        
        let url = `https://${location.host}:443`;
        if (isInstantVoiceIntegrated())
            url = `https://${user.profile.instantvoice_url}:443`

        if (! alreadyConnected) {
            socket = io(url, options);
            
            socket.on('connect', () => {
                let agent = user?.profile.agent.user
                if (isInstantVoiceIntegrated())
                    agent = `${user.profile.instantvoice_accountcode}_${user.profile.instantvoice_agent_code}`
                socket.emit('subscribe', { agent: agent });                
                keepAliveVoice = setInterval(() => {
                    // If server not responding for more than 11 seconds
                    // the connection might have been closed somewhere
                    // we need to close and reopen a new websocket connection
                    let curTime = new Date().getTime();
                    if (lastVoiceSocketResponse && curTime - lastVoiceSocketResponse > 11000) {                        
                        socket.disconnect();                        
                        socket.open();
                        window.socketVoice = socket;
                        return;
                    }

                    // Pinging server every 20secs
                    socket.emit('xping', { agent: user.profile.agent.user });
                }, 10000);
            });

            socket.on('call-event', (event) => {
                const message = (JSON.parse(event.body.trim())).pop()
                switch (message.type) {
                    case 'LOGIN':
                        checkAgentStatus()
                        publishVoice('LOGIN', { message })
                        break
                    case 'LOGOUT':
                        checkAgentStatus()
                        publishVoice('LOGOUT', { message })
                        break
                    case 'PAUSE':
                        checkAgentStatus()
                        publishVoice('PAUSE', { message })
                        break
                    case 'UNPAUSE':
                        checkAgentStatus()
                        publishVoice('UNPAUSE', { message })
                        break
                    case 'NEWCALL':
                        // if (message.direction === "OUTBOUND")
                        //     setDirection("outbound"),
                       
                        let ctiInfo = null;


                        const rawCtiInfo = String(message.cti_info || '').trim()

                        if ( rawCtiInfo.includes('#') ) {
                            ctiInfo = rawCtiInfo.split('#')
                        } else if ( rawCtiInfo.length > 0 ) {
                            ctiInfo = [rawCtiInfo]
                        }

                        ctiRef.current = ctiInfo || []
                        setCti(ctiInfo)
                        setUniqueid(message.uniqueid)
                        setCallDirection(message.direction)
                        publishVoice('NEWCALL', { message, cti: ctiInfo })
                        const queue = message.queue.includes('_') ? message.queue.split('_')[1] : message.queue
                        setCallCti(ctiInfo, queue, message.clientcode, checkTelephonyTab());

                        break
                    case "CONNECT":
                        localStorage.setItem("can_request_call", JSON.stringify(false))
                        window.dispatchEvent(new Event("can_request_call"))
                        scheduleCtiUrlAutoOpen(message.uniqueid)
                        publishVoice('CONNECT', { message })
                        break
                    case 'HANGUP':
                        clearCtiAutoOpenTimeout()
                        ctiRef.current = []
                        ctiDataRef.current = null
                        ctiAutoOpenedRef.current = null
                        setCti([])
                        setShowCti(false)
                        localStorage.setItem("can_request_call", JSON.stringify(true))
                        window.dispatchEvent(new Event("can_request_call"))
                        publishVoice('HANGUP', { message })
                        break
                    default:
                        publishVoice('UNKNOWN_VOICE_EVENT', { message })
                        break
                }
            })

            socket.on('disconnect', () => {
                console.log('O socket foi desconectado.');
                clearInterval(keepAliveVoice);
            });

            socket.on('xpong', (event) => {
                setLastVoiceSocketResponse(new Date().getTime());                
            });
            
            setAlreadyConnected(true);
        }

        if (socket) {
            socket.on('disconnect', () => {
                console.log('O socket foi desconectado.');
                window.socketVoice = null;
                setLastVoiceSocketResponse(null);
            });
        }

        window.socketVoice = socket;
    }

    let closeVoiceSocket = () => {
        if (window.socketVoice?.connected) {
            window.socketVoice.disconnect();
        }
        window.socketVoice = null;
    };

    const checkAgentStatus = () => {
        if (! isInstantVoiceIntegrated())
            return

        api.get(`api/IV/agents/${user.profile.id}/instantvoice_agent_info/`)
            .then((res) => {
                if (res.status === 200) {
                    let status = res.data.AgentInfoResult2.agentstatus
                    switch (status) {
                        case 'LOGIN':
                            status = 'Logado'
                            break
                        case 'LOGOFF':
                            status = 'Deslogado'
                            break
                        case 'LOGOFF_PAUSE':
                            status = 'Deslogado'
                            break
                        case 'PAUSE':
                            status = res.data.AgentInfoResult2.reasons_reason
                            break
                        default:
                            break
                    }
                    setAgentStatus(status)
                }
            })
            .catch((err) => {})
    }

    const handleInstantVoiceAgentLogin = (extension) => {
        if (! isInstantVoiceIntegrated())
            return

        api.post(`api/IV/agents/${user.profile.id}/instantvoice_agent_login/`, {
            extension: extension
        }).then((res) => {
            if (res.status === 200) {
                handleActionsClose()
            }
        }).catch((err) => {})
    }

    const handleInstantVoiceAgentLogoff = () => {
        if (! isInstantVoiceIntegrated())
            return

        api.post(`api/IV/agents/${user.profile.id}/instantvoice_agent_logoff/`)
            .then((res) => {
                if (res.status === 200) {
                    handleActionsClose()
                }
            })
            .catch((err) => {})
    }

    const getPauseReasons = () => {
        if (! isInstantVoiceIntegrated())
            return

        api.get(`api/IV/agents/${user.profile.id}/instantvoice_pause_reasons/`)
            .then((res) => {
                if (res.status === 200) {
                    setPauseReasons(res.data)
                    handleActionsClose()
                }
            })
            .catch((err) => {})
    }

    const handleInstantVoiceAgentUnpause = () => {
        if (! isInstantVoiceIntegrated())
            return

        api.post(`api/IV/agents/${user.profile.id}/instantvoice_agent_unpause/`)
            .then((res) => {
                if (res.status === 200) {
                    handleActionsClose()
                }
            })
            .catch((err) => {})
    }

    const lastStatusRef = useRef(callStatus?.status || "idle");
    const activeCallIdRef = useRef(null); 
    const lastInboundNumberRef = useRef("");
    const ctiRef = useRef([]);
    const ctiDataRef = useRef(null);
    const ctiAutoOpenTimeoutRef = useRef(null);
    const ctiAutoOpenedRef = useRef(null);

    const clearCtiAutoOpenTimeout = () => {
        if (ctiAutoOpenTimeoutRef.current) {
            clearTimeout(ctiAutoOpenTimeoutRef.current);
            ctiAutoOpenTimeoutRef.current = null;
        }
    };

    const syncCtiSignalToRef = (signal) => {
        if (!signal || signal.action !== 'cti') return;

        ctiDataRef.current = signal;
        if (signal.cti) {
            ctiRef.current = Array.isArray(signal.cti) ? signal.cti : [signal.cti];
        }
    };

    const scheduleCtiUrlAutoOpen = (callId) => {
        if (!autoOpenCtiUrlEnabled) return;
        
        const url = getAutoOpenCtiUrl(ctiRef.current) || getAutoOpenCtiUrl(ctiDataRef.current) || getAutoOpenCtiUrl(ctiData);
        if (!url) return;

        // O CONNECT do socket e o status `connected` do WebRTC podem disparar
        // para a mesma chamada. A trava precisa ser pela URL da chamada atual,
        // porque cada origem pode informar um identificador diferente.
        if (ctiAutoOpenedRef.current === url) return;

        ctiAutoOpenedRef.current = url;
        clearCtiAutoOpenTimeout();
        ctiAutoOpenTimeoutRef.current = setTimeout(() => {
            window.open(url, '_blank', 'noopener,noreferrer');
            ctiAutoOpenTimeoutRef.current = null;
        }, 2000);
    };

    useEffect(() => {
        syncCtiSignalToRef(widgetSignal);
    }, [widgetSignal?.timestamp]);

    useEffect(() => {
        syncCtiSignalToRef(sipSignal);
    }, [sipSignal?.timestamp]);

    useEffect(() => {
        if (ctiData) {
            ctiDataRef.current = ctiData;
            if (ctiData.cti) {
                ctiRef.current = Array.isArray(ctiData.cti) ? ctiData.cti : [ctiData.cti];
            }
        }
    }, [ctiData?.timestamp]);

    useEffect(() => {
        return () => {
            clearCtiAutoOpenTimeout();
        };
    }, []);

    const makeId = () => `call-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const normalizeDir = (d, fallback) => {
        const up = (d || "").toUpperCase();
        if (up === "OUTBOUND" || up === "INBOUND") return up;
        return fallback;
    };

    const getCallerIdForHistory = (finalDirection) => {
        if (finalDirection === "OUTBOUND") return dialNumber || "";
        return (
            incomingData?.number ||
            (widgetSignal?.action === "incoming" && widgetSignal?.number ? widgetSignal.number : "") ||
            (sipSignal?.action === "incoming" && sipSignal?.number ? sipSignal.number : "") ||
            lastInboundNumberRef.current ||
            ""
        );
    };

    const buildMsg = (type, callId) => {
        const now = callStatus?.timestamp || Date.now();
        const fallbackDir =
            callStatus.status === "dialing" ? "OUTBOUND" :
                callStatus.status === "ringing" ? "INBOUND" :
                    "INBOUND";        
        
        const finalDirection = normalizeDir(callDirection, fallbackDir);

        return {
            type,
            uniqueid: callId,
            callerid: getCallerIdForHistory(finalDirection),
            direction: finalDirection,
            queue: ctiData?.queue || "",
            clientcode: ctiData?.clientCode || "",
            timestamp: now,
            linenumber: "",
        };
    };

    const patchActiveInboundCallerId = (incomingNumber) => {
        if (!incomingNumber) return;

        lastInboundNumberRef.current = incomingNumber;

        const callId = activeCallIdRef.current || uniqueid;
        if (!callId) return;

        const cur = readHistory();
        const idx = cur.findIndex((x) => x.id === callId);
        if (idx === -1) return;

        const alreadyHasPhone = !!String(cur[idx].phoneNumber || "").trim();
        if (alreadyHasPhone) return;

        const msg = {
            type: "NEWCALL",
            uniqueid: callId,
            callerid: incomingNumber,
            direction: "INBOUND",
            timestamp: Date.now(),
            queue: ctiData?.queue || "",
            clientcode: ctiData?.clientCode || "",
            linenumber: "",
        };

        const next = upsertFromMessage(cur, msg);
        writeHistory(next);

        const u = getSession();
        const enterpriseId = u?.profile?.enterprise?.id;
        const agentId = u?.profile?.agent_id;

        patchContactNameAsync({
            enterpriseId,
            agentId,
            caller: incomingNumber,
            callId,
            uniqueidKey: uniqueid || null,
            direction: "INBOUND",
        });
    };


    useEffect(() => {
        if ((sipSignal?.action === "incoming") && sipSignal?.number) {
            patchActiveInboundCallerId(sipSignal.number);
        }
    }, [sipSignal?.timestamp]);

    useEffect(() => {
        if (widgetSignal?.action === "incoming" && widgetSignal?.number) {
            patchActiveInboundCallerId(widgetSignal.number);
        }
    }, [widgetSignal?.timestamp]);

    useEffect(() => {
        if (!uniqueid) return;
        if (!activeCallIdRef.current) return;
        if (activeCallIdRef.current === uniqueid) return;
        if (callStatus.status === "idle") return;

        const oldId = activeCallIdRef.current;
        const cur = readHistory();
        const idx = cur.findIndex((x) => x.id === oldId);
        const oldItem = cur[idx];
        const oldDir = String(oldItem?.direction || "").toUpperCase();

        if (String(oldId).startsWith("att-")) {
            const cache = window.__telephonyCache?.contacts;
            if (cache?.has(oldId) && !cache.has(uniqueid)) {
                cache.set(uniqueid, cache.get(oldId));
            }
        }

        if (idx === -1) {
            activeCallIdRef.current = uniqueid;
            return;
        }

        const clone = cur.slice();
        clone[idx] = { ...clone[idx], id: uniqueid };

        // migra também o cache (se existir)
        const cache = window.__telephonyCache?.contacts;
        if (cache?.has(oldId)) {
            cache.set(uniqueid, cache.get(oldId));
            cache.delete(oldId);
        }

        activeCallIdRef.current = uniqueid;
        writeHistory(clone);
    }, [uniqueid, callStatus.status]);

    useEffect(() => {
        const prev = lastStatusRef.current;
        const curr = callStatus?.status || "idle";

        const isStarting = (curr === "dialing" || curr === "ringing") && prev !== curr;
        if (isStarting) {
            const fallbackDir =
                callStatus.status === "dialing" ? "OUTBOUND" :
                    callStatus.status === "ringing" ? "INBOUND" :
                        "INBOUND";

            const finalDirection = normalizeDir(callDirection, fallbackDir);

            const callId =
                finalDirection === "INBOUND"
                    ? (incomingData?.info?.uniqueid || uniqueid || makeAttemptId("in"))
                    : (uniqueid || activeCallIdRef.current || makeId());

            activeCallIdRef.current = callId;
            if (finalDirection === "INBOUND") {
                lastInboundNumberRef.current = getCallerIdForHistory(finalDirection);
            }

            const msg = buildMsg("NEWCALL", callId);
            const next = upsertFromMessage(readHistory(), msg);
            writeHistory(next);

            const u = getSession();
            const enterpriseId = u?.profile?.enterprise?.id;
            const agentId = u?.profile?.agent_id;
            patchContactNameAsync({
                enterpriseId,
                agentId,
                caller: msg.callerid,
                callId: callId,
                uniqueidKey: uniqueid || null,
                direction: msg.direction,
            });
        }

        // CONNECT
        const isConnecting = curr === "connected" && prev !== "connected";
        if (isConnecting) {
            const callId = activeCallIdRef.current || uniqueid || makeId();
            activeCallIdRef.current = callId;

            const msg = buildMsg("CONNECT", callId);
            const next = upsertFromMessage(readHistory(), msg);
            writeHistory(next);
            scheduleCtiUrlAutoOpen(callId);
            const cached = window.__telephonyCache?.contacts?.get(uniqueid) || null;
            window.dispatchEvent(new CustomEvent('telephony:contact-data', {
                detail: {
                    contact: cached?.contact || null,
                    phone: cached?.phone || null,
                    source: 'webphone'
                }
            }));
        }

        // HANGUP: quando volta para idle vindo de outro estado
        const isEnding = curr === "idle" && prev !== "idle" && activeCallIdRef.current;
        if (isEnding) {
            const callId = activeCallIdRef.current;
            const msg = buildMsg("HANGUP", callId);
            const next = upsertFromMessage(readHistory(), msg);
            writeHistory(next);

            // limpa call atual
            clearCtiAutoOpenTimeout();
            ctiRef.current = [];
            ctiDataRef.current = null;
            ctiAutoOpenedRef.current = null;
            activeCallIdRef.current = null;
        }

        lastStatusRef.current = curr;
    }, [callStatus.status, callStatus.timestamp, dialNumber, uniqueid, callDirection]);


    useEffect(() => {
        checkAgentStatus()
        getPauseReasons()
        if (! isInstantVoiceIntegrated() && ((! user?.profile.enterprise.has_voice && ! user?.profile.enterprise.whatsapp_calling) || ! user?.profile.agent.preferred_interface || user?.profile.agent.external_username))
            return;
        connectVoiceSocket();
        return () => {
            closeVoiceSocket();
        }
    }, []);

    const isInstantVoiceIntegrated = () => {
        return hasInstantVoiceIntegration(user)
    }

    const useWebphone = () => {
        return shouldUseNewWebphone(user)
    }

    if (user?.profile.enterprise.has_voice || user?.profile.enterprise.whatsapp_calling) {        
        
        const telephone = user.profile.enterprise.telephone;
        let extension = null;
        let info = null
        
        if (user.profile.agent.external_username) {
            extension = user.profile.agent.external_username;
            info = 'Ramal (externo): ' + extension
        } else if (user.profile.agent.preferred_interface) {
            extension = user.profile.agent.preferred_interface;
            extension = extension.split('_').pop();
            info = 'Ramal (interno): ' + extension
        }

        let tooltip = 'Ramal não configurado';
        if (extension) {
            tooltip = info;
            if (telephone)
                tooltip += ' [' + telephone.replace('55', '0') + ']';
        }

        if (extension) {
            if (!isOwner) {
                return (
                    <div className="webphone-menu-container webphone-menu-container--info">
                        <div className="webphone-menu-message">
                            Seu ramal web já está ativo em outra aba do Kwik.
                        </div>
                    </div>
                );
            }
            return (
                <div className="webphone-menu-container">
                    <KwikTooltip 
                        title={extension 
                            ? tooltip 
                            : 'Ramal não configurado'} 
                        placement="left"
                        disableInteractive
                        arrow
                    >
                        <IconButton id="webphone-icon" 
                            ref={iconButton} 
                            disabled={extension && isLoading}
                            onClick={() => {
                                if (!useWebphone() || callStatus.status === "connected" || callStatus.status === "ringing") {
                                    handleClick()
                                } else if (!checkTelephonyTab()) {
                                    // Redirect to the telephony tab
                                    window.location.href = "/chat?selectedTab=telephony";
                                }
                            }}
                        >
                            {extension && isLoading
                                ? (<CircularProgress style={{ marginTop: 0, fontSize: "100%" }} size={20} color="primary"/>) 
                                : (<PhoneIcon className="toolbar-icon green"/>)
                            }
                        </IconButton>
                    </KwikTooltip>
                    <span style={{ marginRight: (isInstantVoiceIntegrated()) ? "0" : ".75rem" }}>
                        {extension.split("_").pop()}
                    </span>
                    {isInstantVoiceIntegrated() &&
                        <>
                            <IconButton disabled>
                                <PersonIcon className={(agentStatus === "Logado") ? "toolbar-icon green" : (agentStatus === "Deslogado" || ! agentStatus) ? "toolbar-icon red" : "toolbar-icon yellow"}/>
                            </IconButton>
                            <span>{agentStatus}</span>
                            <IconButton onClick={handleActionsClick}>
                                <MoreVertIcon className="toolbar-icon"/>
                            </IconButton>
                        </>
                    }
                    <Menu
                        anchorEl={anchorEl}
                        keepMounted
                        sx={{ zIndex: 2001 }}
                        style={shouldAllowPageClickThrough ? { pointerEvents: 'none' } : undefined}
                        PaperProps={shouldAllowPageClickThrough ? { sx: { pointerEvents: 'auto' } } : undefined}
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                        elevation={0}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                        className="webphone-container"
                    >
                        <Box sx={{ display: "flex", flexDirection: "row" }} onKeyDown={(e) => e.stopPropagation()}>
                            {! showCti && cti && cti.length !== 0 &&
                                <Box className="show-cti-button">
                                    <IconButton onClick={() => setShowCti(true)}>CTI</IconButton>
                                    <Box className="arrow-right"/>
                                </Box>
                            }
                            {showCti && cti && cti.length !== 0 &&
                                <Box className="show-cti-container">
                                    <div className="cti-data">
                                        <div className="header">
                                            CTI
                                            <IconButton className="copy-btn" onClick={() => navigator.clipboard.writeText(cti.join('\n'))}>
                                                <CopyIcon />
                                            </IconButton>
                                            <IconButton className="close-btn" onClick={() => setShowCti(false)}>
                                                <CloseIcon />
                                            </IconButton>
                                        </div>
                                        {cti.map((each) => (
                                            <div style={{ whiteSpace: "nowrap" }}>{each}</div>
                                        ))}
                                    </div>
                                    <Box className="arrow-right"/>
                                </Box>
                            }
                            { useWebphone() ? 
                                <Webphone
                                    reAnchor={reAnchor}
                                    handleClose={handleClose}
                                    handleLoading={handleLoading}
                                />
                            :
                                <LegacyWebphone
                                    reAnchor={reAnchor}
                                    handleClose={handleClose}
                                    handleLoading={handleLoading}
                                />
                            }
                        </Box>
                    </Menu>
                    <Menu
                        anchorEl={anchorElActions}
                        keepMounted
                        sx={{zIndex:2001}}
                        open={Boolean(anchorElActions)}
                        onClose={handleActionsClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                        <MenuItem onClick={() => handleInstantVoiceAgentLogin(extension)}>
                            <LoginIcon style={{ marginRight: 8 }}/>
                            Login
                        </MenuItem>
                        <MenuItem onClick={handleInstantVoiceAgentLogoff}>
                            <LogoutIcon style={{ marginRight: 8 }}/>
                            Logout
                        </MenuItem>
                        <MenuItem onClick={handlePauseReasons}>
                            <PauseIcon style={{ marginRight: 8 }}/>
                            Pausa
                        </MenuItem>
                        <MenuItem onClick={handleInstantVoiceAgentUnpause}>
                            <PlayIcon style={{ marginRight: 8 }}/>
                            Despausa
                        </MenuItem>
                    </Menu>
                    <Dialog open={openDialog} onClose={handleDialogClose}>
                        <DialogTitle>Pausa</DialogTitle>
                        <Divider/>
                        <DialogContent>
                            <FormControl sx={{ mt: 1, minWidth: 250 }} size="small">
                                <InputLabel id="pause-reasons-list">Motivo da pausa</InputLabel>
                                <Select
                                    labelId="pause-reasons-list"
                                    value={pauseReason}
                                    label="Motivo da pausa"
                                    onChange={(event) => setPauseReason(event.target.value)}
                                >
                                    {pauseReasons.map((each) => (
                                        <MenuItem value={each.number}>{each.location}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </DialogContent>
                        <DialogActions sx={{ mr: 1 }}>
                            <Button onClick={handleDialogClose}>Cancelar</Button>
                            <Button onClick={handleReasonSubmit}>Pausar</Button>
                        </DialogActions>
                    </Dialog>
                </div>
            );
        }

        return (
            <Tooltip title="Recurso indisponível para seu usuário.">
                <IconButton
                    sx={{
                        backgroundColor: "#5166a6",
                        color: "white",
                        fontSize: "1.25rem",

                        "&:hover": {
                            backgroundColor: "#1b426c",
                        },

                        "& svg": {
                            color: "white",
                            fontSize: "1.25rem",
                        },
                    }}
                >
                    <PhoneIcon />
                </IconButton>
            </Tooltip>
        )
    }

    return (
        <Tooltip title="Recurso indisponível para sua conta.">
            <IconButton
                sx={{
                    backgroundColor: "#5166a6",
                    color: "white",
                    fontSize: "1.25rem",

                    "&:hover": {
                        backgroundColor: "#1b426c",
                    },

                    "& svg": {
                        color: "white",
                        fontSize: "1.25rem",
                    },
                }}
            >
                <PhoneIcon />
            </IconButton>
        </Tooltip>
    )
}

export default WebphoneMenu;
